import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import CloseIcon from '@mui/icons-material/Close';
import AuthContext from '../../context/authContext';
import useIssue from '../../hooks/useIssue';
import useBranch from '../../hooks/useBranch';
import useEntertainment from '../../hooks/useEntertainment';
import { ISSUE_PRIORITIES } from '../../util/issueConstants';
import { downscaleImageToBase64 } from '../../util/imageDownscale';

// Display name helpers; branches/entertainments may arrive in different shapes
// depending on the endpoint (light vs full, populated vs plain ObjectId).
const branchLabel = branch => branch?.locationName || branch?.longName || '';
const entertainmentLabel = ent => ent?.longName || ent?.entertainmentName || '';
const entertainmentBranchId = ent => {
  if (!ent?.branch) return '';
  return typeof ent.branch === 'object' ? String(ent.branch._id || '') : String(ent.branch);
};

// Collect all resource ids of an entertainment from both the plain resourceIds
// array and the orderedResources sub-document, deduplicated.
const collectResourceIds = ent => {
  const ids = [];
  (ent?.resourceIds || []).forEach(entry => {
    const id = typeof entry === 'object' ? entry?._id : entry;
    if (id) ids.push(String(id));
  });
  (ent?.orderedResources?.resources || []).forEach(entry => {
    const id = typeof entry?.resourceId === 'object' ? entry.resourceId?._id : entry?.resourceId;
    if (id) ids.push(String(id));
  });
  return [...new Set(ids)];
};

/**
 * Mobile-first issue reporting form. Also serves as the landing page for the
 * printed QR codes in the rooms: /issues/report?branchId=..&entertainmentId=..
 * &resourceId=.. preselects the location so staff only pick priority + title.
 */
export default function IssueReportView() {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { createIssue, uploadPhoto, getStatusOverview, getCategories } = useIssue();
  const { getBranches } = useBranch();
  const { getEntertainments } = useEntertainment();

  const canCreate = authCtx.checkRight('issue:create');

  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [entertainments, setEntertainments] = useState([]);
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);

  const [priority, setPriority] = useState('');
  const [branchId, setBranchId] = useState('');
  const [entertainmentId, setEntertainmentId] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  // Location lock from a scanned QR code: hides the location selects and shows
  // an info banner instead until the user explicitly wants to change it.
  const [qrLocked, setQrLocked] = useState(false);

  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const fileInputRef = useRef(null);
  const photosRef = useRef([]);
  photosRef.current = photos;
  const preselectApplied = useRef(false);

  useEffect(() => {
    if (!canCreate) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Reference data is loaded independently: a reporter without read
        // rights may not get the status overview (resource mapping) or the
        // categories; the form still works without them.
        const [branchRes, entRes, overviewRes, categoryRes] = await Promise.all([
          getBranches().catch(() => null),
          getEntertainments(true).catch(() => null),
          getStatusOverview().catch(() => null),
          getCategories().catch(() => null),
        ]);
        if (cancelled) return;
        const branchList = Array.isArray(branchRes) ? branchRes : branchRes?.branches || [];
        setBranches(branchList);
        let entList = Array.isArray(entRes) ? entRes : entRes?.entertainments || [];
        // The status overview carries resourceIds/orderedResources which the
        // light entertainment list does not; prefer it for the resource mapping.
        if (Array.isArray(overviewRes?.entertainments) && overviewRes.entertainments.length > 0) {
          entList = overviewRes.entertainments;
        }
        setEntertainments(entList.filter(ent => ent?.active !== false));
        setResources(Array.isArray(overviewRes?.resources) ? overviewRes.resources : []);
        setCategories(Array.isArray(categoryRes?.categories) ? categoryRes.categories : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCreate]);

  // Revoke all thumbnail object URLs on unmount.
  useEffect(
    () => () => {
      photosRef.current.forEach(photo => URL.revokeObjectURL(photo.url));
    },
    []
  );

  const resourcesById = useMemo(
    () => new Map(resources.map(resource => [String(resource._id), resource])),
    [resources]
  );

  const branchEntertainments = useMemo(
    () => entertainments.filter(ent => entertainmentBranchId(ent) === branchId),
    [entertainments, branchId]
  );

  const selectedEntertainment = useMemo(
    () => entertainments.find(ent => String(ent._id) === entertainmentId) || null,
    [entertainments, entertainmentId]
  );

  const entertainmentResources = useMemo(() => {
    if (!selectedEntertainment) return [];
    return collectResourceIds(selectedEntertainment)
      .map(id => resourcesById.get(id))
      .filter(resource => resource && resource.active !== false);
  }, [selectedEntertainment, resourcesById]);

  const activeCategories = useMemo(
    () => categories.filter(category => category.active !== false),
    [categories]
  );

  // Apply the QR preselection once the reference data is available.
  useEffect(() => {
    if (loading || preselectApplied.current) return;
    preselectApplied.current = true;
    const paramBranchId = searchParams.get('branchId') || '';
    const paramEntertainmentId = searchParams.get('entertainmentId') || '';
    const paramResourceId = searchParams.get('resourceId') || '';
    if (!paramBranchId && !paramEntertainmentId) return;
    if (paramBranchId) setBranchId(paramBranchId);
    if (paramEntertainmentId) {
      setEntertainmentId(paramEntertainmentId);
      const ent = entertainments.find(e => String(e._id) === paramEntertainmentId);
      // Derive the branch from the entertainment when the QR code omitted it.
      if (!paramBranchId && ent) setBranchId(entertainmentBranchId(ent));
      if (paramResourceId) setResourceId(paramResourceId);
      setQrLocked(true);
    }
  }, [loading, searchParams, entertainments]);

  const qrBannerText = useMemo(() => {
    if (!qrLocked) return '';
    const entName = entertainmentLabel(selectedEntertainment) || 'Entertainment';
    const resource = resourcesById.get(resourceId);
    return resource ? `${entName} / ${resource.name}` : entName;
  }, [qrLocked, selectedEntertainment, resourcesById, resourceId]);

  const handleBranchChange = useCallback(event => {
    setBranchId(event.target.value);
    setEntertainmentId('');
    setResourceId('');
  }, []);

  const handleEntertainmentChange = useCallback(event => {
    setEntertainmentId(event.target.value);
    setResourceId('');
  }, []);

  const handleFilesSelected = useCallback(event => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setPhotos(prev => [...prev, ...files.map(file => ({ file, url: URL.createObjectURL(file) }))]);
    // Allow picking the same file again after removing it.
    event.target.value = '';
  }, []);

  const handleRemovePhoto = useCallback(index => {
    setPhotos(prev => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.url);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const resetForm = useCallback(() => {
    setPriority('');
    setCategoryId('');
    if (!qrLocked) {
      setBranchId('');
      setEntertainmentId('');
      setResourceId('');
    }
    if (titleRef.current) titleRef.current.value = '';
    if (descriptionRef.current) descriptionRef.current.value = '';
    setPhotos(prev => {
      prev.forEach(photo => URL.revokeObjectURL(photo.url));
      return [];
    });
  }, [qrLocked]);

  const handleSubmit = useCallback(async () => {
    const title = titleRef.current?.value?.trim() || '';
    const missing = [];
    if (!priority) missing.push('Priorität');
    if (!branchId) missing.push('Standort');
    if (!title) missing.push('Titel');
    if (missing.length > 0) {
      toast.error(`Bitte ausfüllen: ${missing.join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      const result = await createIssue({
        title,
        description: descriptionRef.current?.value?.trim() || undefined,
        priority,
        branchId,
        entertainmentId: entertainmentId || undefined,
        resourceId: resourceId || undefined,
        categoryId: categoryId || undefined,
      });
      const issueId = result?.issue?._id;
      // Photos are uploaded after issue creation, one by one with progress.
      if (issueId && photos.length > 0) {
        for (let i = 0; i < photos.length; i += 1) {
          setUploadProgress({ current: i + 1, total: photos.length });
          try {
            const payload = await downscaleImageToBase64(photos[i].file);
            await uploadPhoto(issueId, payload);
          } catch (err) {
            // The hook already showed a German toast; continue with the rest.
            console.error('issue photo upload failed', err);
          }
        }
      }
      toast.success('Issue gemeldet');
      if (priority === 'p1') {
        toast.info('Eskalation gestartet – Geschäftsleitung & IT werden benachrichtigt');
        // The backend reports what it did to the booking grid: a P1 takes the
        // affected unit out of sale until the ticket is closed again.
        if (result?.outageCreated) {
          toast.warning('Ausfall gesetzt – der Bereich ist ab sofort für Buchungen gesperrt');
        } else if (result?.alreadyBlocked) {
          toast.info('Bereich ist bereits als Ausfall gesperrt – Ticket wurde angehängt');
        } else if (!entertainmentId) {
          toast.info('Ohne Entertainment-Auswahl konnte kein Buchungs-Blocker gesetzt werden');
        }
      }
      if (authCtx.checkRight('issue:read') || authCtx.checkRight('issue:read:own')) {
        navigate('/issues');
      } else {
        resetForm();
      }
    } catch (err) {
      // createIssue already showed a German error toast.
      console.error('issue creation failed', err);
    } finally {
      setUploadProgress(null);
      setSubmitting(false);
    }
  }, [
    priority,
    branchId,
    entertainmentId,
    resourceId,
    categoryId,
    photos,
    createIssue,
    uploadPhoto,
    authCtx,
    navigate,
    resetForm,
  ]);

  if (!canCreate) return <Navigate to='/' replace />;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', px: 2, py: 3 }}>
      <Typography variant='h5' sx={{ fontWeight: 700, mb: 0.5 }}>
        Issue melden
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Mangel oder Störung erfassen – bei P1 wird sofort eskaliert.
      </Typography>

      {qrLocked && (
        <Alert
          severity='info'
          sx={{ mb: 3 }}
          action={
            <Button color='inherit' size='small' onClick={() => setQrLocked(false)}>
              Ändern
            </Button>
          }
        >
          Meldung für: <strong>{qrBannerText}</strong>
        </Alert>
      )}

      <Stack spacing={3}>
        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Priorität *
          </Typography>
          {loading ? (
            <Stack spacing={1}>
              {ISSUE_PRIORITIES.map(p => (
                <Skeleton key={p.key} variant='rounded' height={64} />
              ))}
            </Stack>
          ) : (
            <Stack spacing={1}>
              {ISSUE_PRIORITIES.map(p => {
                const selected = priority === p.key;
                return (
                  <ButtonBase
                    key={p.key}
                    onClick={() => setPriority(p.key)}
                    sx={{ width: '100%', textAlign: 'left', borderRadius: 2, display: 'block' }}
                  >
                    <Paper
                      variant='outlined'
                      sx={{
                        display: 'flex',
                        overflow: 'hidden',
                        borderRadius: 2,
                        borderWidth: 2,
                        borderColor: selected ? p.color : 'divider',
                        backgroundColor: selected ? alpha(p.color, 0.12) : 'background.paper',
                      }}
                    >
                      <Box sx={{ width: 8, flexShrink: 0, backgroundColor: p.color }} />
                      <Box sx={{ p: 1.5, minHeight: 56 }}>
                        <Typography variant='subtitle1' sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                          {p.label}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {p.description}
                        </Typography>
                      </Box>
                    </Paper>
                  </ButtonBase>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* P1 does more than notify: it blocks the unit in the booking grid, so
            the consequence is stated before the report is sent. */}
        {priority === 'p1' && (
          <Alert severity={entertainmentId ? 'warning' : 'info'}>
            {entertainmentId
              ? 'Mit dem Absenden wird der gewählte Bereich sofort als Ausfall gesetzt und für neue Buchungen gesperrt. Die Sperre wird automatisch aufgehoben, sobald das Ticket erledigt ist.'
              : 'Bitte Entertainment (und ggf. Raum) auswählen – nur dann kann der Bereich automatisch für neue Buchungen gesperrt werden.'}
          </Alert>
        )}

        {!qrLocked && (
          <>
            <FormControl fullWidth>
              <InputLabel id='issue-report-branch-label'>Standort *</InputLabel>
              <Select
                labelId='issue-report-branch-label'
                label='Standort *'
                value={branchId}
                onChange={handleBranchChange}
                disabled={loading}
              >
                {branches.map(branch => (
                  <MenuItem key={branch._id} value={String(branch._id)}>
                    {branchLabel(branch)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={loading || !branchId}>
              <InputLabel id='issue-report-entertainment-label'>Entertainment</InputLabel>
              <Select
                labelId='issue-report-entertainment-label'
                label='Entertainment'
                value={entertainmentId}
                onChange={handleEntertainmentChange}
              >
                <MenuItem value=''>
                  <em>Kein Entertainment</em>
                </MenuItem>
                {branchEntertainments.map(ent => (
                  <MenuItem key={ent._id} value={String(ent._id)}>
                    {entertainmentLabel(ent)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={loading || !entertainmentId}>
              <InputLabel id='issue-report-resource-label'>Raum / Ressource</InputLabel>
              <Select
                labelId='issue-report-resource-label'
                label='Raum / Ressource'
                value={resourceId}
                onChange={event => setResourceId(event.target.value)}
              >
                <MenuItem value=''>Gesamtes Entertainment</MenuItem>
                {entertainmentResources.map(resource => (
                  <MenuItem key={resource._id} value={String(resource._id)}>
                    {resource.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        <FormControl fullWidth disabled={loading}>
          <InputLabel id='issue-report-category-label'>Kategorie / Spezialgebiet</InputLabel>
          <Select
            labelId='issue-report-category-label'
            label='Kategorie / Spezialgebiet'
            value={categoryId}
            onChange={event => setCategoryId(event.target.value)}
          >
            <MenuItem value=''>
              <em>Keine Kategorie</em>
            </MenuItem>
            {activeCategories.map(category => (
              <MenuItem key={category._id} value={String(category._id)}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Hilft der IT bei der Zuordnung</FormHelperText>
        </FormControl>

        <TextField inputRef={titleRef} label='Titel *' fullWidth />
        <TextField inputRef={descriptionRef} label='Beschreibung' fullWidth multiline minRows={3} />

        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Fotos
          </Typography>
          <ButtonBase
            onClick={() => fileInputRef.current?.click()}
            sx={{ width: '100%', borderRadius: 2, display: 'block' }}
          >
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AddAPhotoIcon color='action' fontSize='large' />
              <Typography variant='body2' color='text.secondary'>
                Foto aufnehmen oder auswählen
              </Typography>
            </Box>
          </ButtonBase>
          <input
            ref={fileInputRef}
            type='file'
            accept='image/*'
            capture='environment'
            multiple
            hidden
            onChange={handleFilesSelected}
          />
          {photos.length > 0 && (
            <Stack direction='row' spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
              {photos.map((photo, index) => (
                <Box key={photo.url} sx={{ position: 'relative' }}>
                  <Box
                    component='img'
                    src={photo.url}
                    alt={`Foto ${index + 1}`}
                    sx={{
                      width: 88,
                      height: 88,
                      objectFit: 'cover',
                      borderRadius: 1.5,
                      display: 'block',
                    }}
                  />
                  <IconButton
                    size='small'
                    onClick={() => handleRemovePhoto(index)}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: 'background.paper',
                      boxShadow: 1,
                      '&:hover': { backgroundColor: 'background.paper' },
                    }}
                  >
                    <CloseIcon fontSize='inherit' />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {uploadProgress && (
          <Box>
            <LinearProgress
              variant='determinate'
              value={(uploadProgress.current / uploadProgress.total) * 100}
            />
            <Typography variant='caption' color='text.secondary'>
              Foto {uploadProgress.current} von {uploadProgress.total} wird hochgeladen…
            </Typography>
          </Box>
        )}

        <Button
          variant='contained'
          size='large'
          fullWidth
          onClick={handleSubmit}
          disabled={submitting || loading}
          startIcon={submitting ? <CircularProgress size={18} color='inherit' /> : null}
          sx={{ py: 1.5, fontWeight: 700 }}
        >
          Issue melden
        </Button>
      </Stack>
    </Box>
  );
}
